import { Formik } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import Button from '../../../../components/form-control/Button';
import GTable from '../../../../components/elements/GTable';
import { useCommissions } from '../../../../hooks/commissionsHook';
import { markBulkCommissionAsPaidAndSettle, markCommissionAsPaidAndSettle } from '../../../../services/commissions';
import { useQueryClient } from '@tanstack/react-query';
import { formatCurrencyAmount, showToast } from '../../../../utils/miscFunctions';

type IMarkCommissionAsPaidModal = {
    onClose: () => void;
    commissions: any;
};

const MarkCommissionAsPaidModal = ({ onClose, commissions }: IMarkCommissionAsPaidModal) => {
    const [submitting, setSubmitting] = useState(false);
    const { columns } = useCommissions({ showCheckColumn: false });
    const queryClient = useQueryClient();

    const commsArray = commissions && commissions.length > 1 ? [...commissions] : [commissions];
    const isBulk = commsArray.length > 1;

    const submitHandler = async () => {
        setSubmitting(true);
        const commissionIds = isBulk ? commsArray.map((c: any) => c.id) : commissions.id;

        try {
            const res: any = isBulk
                ? await markBulkCommissionAsPaidAndSettle(commissionIds)
                : await markCommissionAsPaidAndSettle(commissionIds);

            if (res) {
                showToast.success(res?.message || `Successfully marked commission${isBulk ? 's' : ''} as paid and created settlement`);
                queryClient.invalidateQueries({ queryKey: ['commissions'] });
                onClose()
            }
        } catch (error: any) {
            showToast.error(error?.description);
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <div className="w-full p-6">
            {/* Header */}
            <h2 className="text-xl font-semibold text-gray-800 mb-1">
                Mark Commission as Paid
            </h2>

            <p className="text-sm text-gray-500 mb-4">
                Review the commission details below and confirm the action.
            </p>

            <Formik
                initialValues={{}}
                validationSchema={Yup.object({})}
                onSubmit={submitHandler}
            >
                {({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} className="space-y-5">

                        {/* Total Commission */}
                        <div className="flex items-center justify-end gap-4">
                            <span className="text-md font-medium text-gray-500">Total Commission :</span>
                            <span className="text-md font-bold text-gray-900">{formatCurrencyAmount(commsArray.reduce((acc: number, commission: any) => acc + commission.netCommissionAmount, 0).toFixed(2))}</span>
                        </div>

                        {/* Table Container */}
                        <div className="border border-gray-200 rounded-lg p-3 bg-white shadow-sm max-h-[65vh] overflow-y-auto">
                            <GTable columns={columns} data={commsArray} showSearch={false}/>
                        </div>

                        {/* Buttons */}
                        <div className="flex items-center justify-end gap-4 pt-3">
                            <Button
                                type="button"
                                label="Cancel"
                                onClick={onClose}
                                size='lg'
                                disabled={submitting}
                            />
                            <Button
                                type="submit"
                                label="Submit"
                                bgColor='primary'
                                size='lg'
                                loading={submitting}
                                disabled={submitting}
                            />
                        </div>

                    </form>
                )}
            </Formik>
        </div>
    );
};

export default MarkCommissionAsPaidModal;
